import React, { useState } from "react";
import moment from "moment";
import { searchNewHashtag } from "actions/sourceMedia";
import { useDispatch } from "react-redux";
import { Hashtag } from "../types";
import { AccountType } from "shared/types/accounts";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
  CommandGroup,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";

export function HashtagSelector({
  hashtags,
  account,
  openExpiredListModal,
  showExpiredListModal,
  onHashtagChanged,
}: {
  hashtags: Hashtag[];
  account: AccountType | null;
  openExpiredListModal: () => void;
  showExpiredListModal: boolean;
  onHashtagChanged: (hashtagId: string) => void;
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [customHashtag, setCustomHashtag] = useState("");

  const createNewHashtag = () => {
    if (!customHashtag || !account?._id) return;
    dispatch(
      searchNewHashtag(account._id, customHashtag.toLowerCase(), "picker")
    );
    setCustomHashtag("");
    setSearchValue("");
  };

  const handleSelect = (hashtagId: string) => {
    setOpen(false);
    setSearchValue("");
    setCustomHashtag("");
    onHashtagChanged && onHashtagChanged(hashtagId);
  };

  // Filter hashtags based on search
  const filteredHashtags = hashtags.filter((hashtag) =>
    hashtag.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Check if search value is a new hashtag (not in existing list)
  const isNewHashtag =
    searchValue &&
    !hashtags.some((h) => h.name.toLowerCase() === searchValue.toLowerCase());

  React.useEffect(() => {
    if (isNewHashtag) {
      setCustomHashtag(searchValue);
    } else {
      setCustomHashtag("");
    }
  }, [searchValue, isNewHashtag]);

  return (
    <div className="flex items-center gap-2.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full h-[38px] justify-between font-inter text-sm",
              dropdownTriggerClassName
            )}
          >
            <span
              className={searchValue ? "text-neutral-800" : "text-neutral-500"}
            >
              {searchValue || "Search for hashtag..."}
            </span>
            <i
              className={cn(
                "fa-regular fa-chevron-down ml-2 h-4 w-4 shrink-0",
                dropdownChevronClassName
              )}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" disableAnimation>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search hashtag..."
              value={searchValue}
              onValueChange={setSearchValue}
              className="border-neutral-200 font-inter text-sm"
            />
            <CommandList>
              {filteredHashtags.length === 0 && !isNewHashtag && (
                <CommandEmpty className="font-inter text-sm text-neutral-600">
                  No hashtag created yet.
                </CommandEmpty>
              )}
              {filteredHashtags.length > 0 && (
                <CommandGroup>
                  {filteredHashtags.map((hashtag) => {
                    const expiredDate = hashtag.search_date
                      ? moment().to(
                          moment(hashtag.search_date).add(7, "days"),
                          true
                        )
                      : "Unknown";

                    return (
                      <CommandItem
                        key={hashtag.hashtag_id}
                        value={hashtag.hashtag_id}
                        onSelect={() => handleSelect(hashtag.hashtag_id)}
                        className="font-inter"
                      >
                        <div className="flex justify-between w-full items-center">
                          <span className="text-xs text-neutral-800 truncate max-w-[150px]">
                            #{hashtag.name}
                          </span>
                          <span className="text-xs text-neutral-400">
                            {expiredDate}
                          </span>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
              {showExpiredListModal && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    openExpiredListModal();
                  }}
                  className="p-2.5 text-center text-xs underline text-blue-500 cursor-pointer hover:bg-blue-50 transition-colors"
                  data-testid="expired-hashtag-model-trigger"
                >
                  Expired hashtags
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Button
        variant="outline"
        disabled={!customHashtag}
        onClick={createNewHashtag}
        className="w-[94px] h-[38px] px-3 text-sm font-medium border-neutral-200 hover:border-neutral-300"
      >
        + Add
      </Button>
    </div>
  );
}
